<script lang="ts">
  import { getContext, onMount } from 'svelte';
  import './css/discountMessage.scss';
  import './css/discountThankYou.scss';
  import './css/formDiscount.scss';
  import { BulletPoint } from '../../components/index';
  import {
    MailAltIcon,
    PhoneAltFilledIcon,
    UserIcon,
    ZipCodeIcon,
  } from '../../components/icons';
  import { TimeOut } from '.';
  import { formatPhoneNumberHref } from '../../helpers/phoneNumberHelper';
  import {
    formatPhoneNumber,
    isValidPhoneNumber,
    isValidEmailAddress,
  } from 'core-components/helpers';
  import { Input } from 'core-components';
  import type { FormDiscount } from '../types';
  import type { MktSettings } from 'customer-components/lib/types';
  import { browser } from '$app/environment';
  import SmsVerification from './SMSVerification.svelte';

  export let properties: FormDiscount;
  export let settings: MktSettings;
  export let timeOutPopupVisible: boolean;

  const hash = getContext<string>('hash');
  const lpKey = getContext<string>('lpKey');
  //start time counter for measuring time on page
  let startDate = new Date();

  $: ({
    buttonText,
    backgroundImage,
    callouts,
    description,
    headline,
    subHeadline,
    isTimeOutPopup,
  } = properties);
  $: ({ phoneNumber } = settings);

  let formSubmitted = false,
    leadId = null,
    name = '',
    email = '',
    phone = '',
    message = '',
    zipCode = '',
    emailError = false,
    phoneError = false,
    messageError = false,
    commentError = false,
    zipCodeError = false,
    showSmsVerification = false;
  const handlePhoneChange = () => {
    phone = formatPhoneNumber(phone);
  };

  function isValidZipCode(zip: string) {
    const zipRegex = /^\d{5}(-\d{4})?$/;
    //allow L6M0J6 or L6M 0J6
    const postalCodeRegex = /^[A-Za-z]\d[A-Za-z][ ]?\d[A-Za-z]\d$/;
    return zipRegex.test(zip) || postalCodeRegex.test(zip);
  }

  async function handleSubmit(event: Event) {
    if (
      (!isValidPhoneNumber(phone) && phone.length > 0) ||
      (properties.phoneRequired && phone.length <= 0)
    ) {
      phoneError = true;
      return;
    } else if (
      (!isValidEmailAddress(email) && email.length > 0) ||
      (properties.emailRequired && email.length <= 0)
    ) {
      emailError = true;
      return;
    } else if (!email && !phone) {
      messageError = true;
      return;
    } else if (!message?.length && properties.commentsRequired) {
      commentError = true;
      return;
    } else if (properties?.zipCodeRequired && !zipCode) {
      zipCodeError = true;
      return;
    } else if (!isValidZipCode(zipCode)) {
      zipCodeError = true;
      return;
    } else if (email || phone) {
      let endDate = new Date();
      const start = new Date(startDate).getTime();
      const end = new Date(endDate).getTime();
      const totalSeconds = Math.floor((end - start) / 1000);

      const res = await properties.submitLead(event, {
        timeOnPage: totalSeconds,
        comments: message,
        fullName: name,
        email: email,
        phone: phone,
        zipCode: zipCode,
      });
      formSubmitted = true;

      // if (res.data.leadId && phone) {
      //   phone = phone;
      //   leadId = res.data.leadId;
      //   showSmsVerification = true;
      // }
    }
  }

  function resetForm() {
    emailError = false;
    phoneError = false;
    messageError = false;
  }

  function stripHtml(html) {
    if (!browser) return;
    let tmp = document.createElement('DIV');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  }
</script>

<svelte:window on:keydown={resetForm} />

{#if isTimeOutPopup}
  <TimeOut {properties} {timeOutPopupVisible} />
{/if}

<section class="discount3" id="form">
  <div class="background-image">
    <img loading="lazy" src={backgroundImage?.value} alt="text" />
  </div>
  <div class="wrapper">
    {#if formSubmitted}
      <div class="discount-thank-you">
        <div class="discount-thank-you-top">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="61"
            height="60"
            viewBox="0 0 61 60"
            fill="none"
          >
            <circle cx="30.5" cy="30" r="25" fill="white" />
            <path
              d="M30.5 5C16.715 5 5.5 16.215 5.5 30C5.5 43.785 16.715 55 30.5 55C44.285 55 55.5 43.785 55.5 30C55.5 16.215 44.285 5 30.5 5ZM25.5025 41.0325L16.22 31.77L19.75 28.23L25.4975 33.9675L38.7325 20.7325L42.2675 24.2675L25.5025 41.0325Z"
              fill="var(--color-accent)"
            />
          </svg>
          <h2 class="h2">Thank you</h2>
          <p class="sub-text">
            We will reach out to you shortly/within 24 hours, with a discount
            code/service request.
          </p>
        </div>
        <div class="bottom-boxes">
          <div class="get-a-response">
            <p class="sub-text">Or Call us now</p>
            <a href="tel:{formatPhoneNumberHref(phoneNumber?.value)}">
              <h2 class="h2">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="32"
                  height="32"
                  viewBox="0 0 32 32"
                  fill="none"
                >
                  <path
                    d="M27.3164 21.8533L21.8964 16.9253C21.3644 16.4413 20.543 16.4666 20.0417 16.9826L16.851 20.2639C16.083 20.1173 14.539 19.6359 12.9497 18.0506C11.3604 16.4599 10.879 14.9119 10.7364 14.1493L14.015 10.9573C14.5324 10.4559 14.5564 9.63592 14.0724 9.10258L9.14571 3.68392C8.66171 3.14925 7.83904 3.09858 7.29104 3.56792L4.39771 6.04925C4.16704 6.28125 4.02971 6.58792 4.01104 6.91458C3.99104 7.24792 3.60971 15.1439 9.73238 21.2693C15.0737 26.6093 21.7644 26.9999 23.607 26.9999C23.8764 26.9999 24.0417 26.9919 24.0857 26.9893C24.4124 26.9706 24.719 26.8333 24.9497 26.6012L27.4297 23.7066C27.9017 23.1586 27.8497 22.3372 27.3164 21.8533Z"
                    fill="var(--color-accent)"
                  />
                </svg>
                {@html phoneNumber?.value}
              </h2>
            </a>
          </div>
          <div class="small-text-box">
            <div>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="25"
                height="24"
                viewBox="0 0 25 24"
                fill="none"
              >
                <path
                  d="M9.57904 13.9485L6.02576 10.3952L4.5 11.921L9.57904 17L20.0533 6.52576L18.5275 5L9.57904 13.9485Z"
                  fill="var(--color-accent,#8DC63F)"
                />
              </svg>
              <p class="text">Response within 24 hrs</p>
            </div>
          </div>
        </div>
      </div>
    {:else}
      <div class="discount-full-form">
        <div class="discount-top">
          <h2 class="h2">
            {@html headline?.value}
            <span>{@html subHeadline?.value}</span>
          </h2>
          {#if stripHtml(description?.richText)?.trim()?.length > 0}
            <p class="sub-text">
              {@html description?.richText}
            </p>
            <svg
              class="arrow"
              xmlns="http://www.w3.org/2000/svg"
              width="73"
              height="86"
              viewBox="0 0 73 86"
              fill="none"
            >
              <path
                d="M13.7867 76.657C12.1437 71.6015 8.0156 67.7253 4.21912 64.2111C4.21912 64.2111 3.98488 64.016 4.08771 63.8726C4.19053 63.7293 4.43948 63.9039 4.43948 63.9039C7.96039 65.9449 11.284 68.36 13.9673 71.4905C13.5114 42.3143 44.9806 18.4986 72.0099 17.928C72.0099 17.928 72.394 17.8778 72.4162 18.1651C72.4384 18.4524 71.9887 18.5524 71.9887 18.5524C58.2411 20.5007 42.1603 29.6304 34.7386 37.3995C24.114 48.5278 20.1403 57.5839 16.2843 70.4308C17.5212 66.3012 22.6411 63.3323 27.0921 63.6018C27.0921 63.6018 27.3317 63.6069 27.355 63.7206C27.3783 63.8342 27.1714 63.9766 27.1714 63.9766C24.2833 65.9836 22.1569 68.7812 20.0827 71.5736C18.3449 73.6784 16.8331 75.9611 15.5739 78.381C15.5037 78.5123 15.397 78.6208 15.2675 78.6926C15.138 78.7643 14.9916 78.7961 14.8469 78.7839C14.7023 78.7717 14.5659 78.716 14.4554 78.6239C14.3448 78.5319 14.2649 78.4076 14.226 78.2671L13.7867 76.657Z"
                fill="var(--color-accent)"
              />
              <path
                d="M47.6781 19.0106C46.4539 18.9706 45.2445 19.4006 44.2921 20.2146C44.2499 20.2522 44.2205 20.3022 44.2082 20.3572C44.196 20.4121 44.2015 20.469 44.2241 20.5193C44.2467 20.5696 44.285 20.6105 44.3334 20.636C44.3818 20.6615 44.4375 20.6702 44.4923 20.6607C45.6245 20.4368 46.7244 20.0507 47.7585 19.5142C47.8185 19.4947 47.87 19.4539 47.9034 19.3995C47.9369 19.3451 47.9498 19.2809 47.94 19.2189C47.9301 19.157 47.898 19.1016 47.8497 19.0633C47.8015 19.0249 47.7405 19.0062 47.6781 19.0106Z"
                fill="var(--color-accent)"
              />
              <path
                d="M42.1856 21.398C33.986 24.2048 26.3487 30.125 22.3692 37.9945C22.3439 38.0339 22.3348 38.0814 22.3439 38.1264C22.353 38.1715 22.3795 38.2104 22.4177 38.2347C22.4558 38.259 22.5025 38.2666 22.5474 38.2559C22.5923 38.2452 22.6317 38.2171 22.657 38.1776C27.9939 31.3323 34.8826 26.0382 42.3231 21.7176C42.3656 21.6978 42.3992 21.6619 42.4163 21.6179C42.4335 21.5738 42.4329 21.5253 42.4147 21.4829C42.3965 21.4405 42.3621 21.4078 42.3191 21.3918C42.2761 21.3759 42.2281 21.3781 42.1856 21.398Z"
                fill="var(--color-accent)"
              />
            </svg>
          {/if}
        </div>

        <form
          class="discount-form-inputs"
          on:submit|preventDefault={handleSubmit}
        >
          <input type="hidden" name="hash" value={hash} />
          <div class="fullname-and-email">
            <div class="input-box">
              <Input bind:value={name} placeholder="Name" name="fullName">
                <UserIcon slot="icon" />
              </Input>
            </div>

            <div class="input-box" style="--destructive-foreground:red;">
              <Input
                bind:value={email}
                type="email"
                placeholder="Email"
                name="email"
                error={emailError}
                errorMessage="Please provide a valid email."
              >
                <MailAltIcon slot="icon" />
              </Input>
              {#if !properties?.emailRequired}
                <span class="input-side-text">Optional</span>
              {/if}
            </div>
          </div>
          <div class="input-box" style="--destructive-foreground:red;">
            <Input
              bind:value={phone}
              on:input={handlePhoneChange}
              type="tel"
              placeholder="Phone Number"
              name="phone"
              error={phoneError}
              errorMessage="Please provide a valid phone number"
            >
              <PhoneAltFilledIcon slot="icon" />
            </Input>
            {#if !properties?.phoneRequired}
              <span class="input-side-text">Optional</span>
            {/if}
          </div>

          <div class="input-box" style="--destructive-foreground:red;">
            <Input
              bind:value={zipCode}
              type="text"
              placeholder="Zip Code"
              name="zipCode"
              error={zipCodeError}
              errorMessage="Please provide a valid zip code"
            >
              <ZipCodeIcon slot="icon" />
            </Input>
            {#if !properties?.zipCodeRequired}
              <span class="input-side-text">Optional</span>
            {/if}
          </div>

          <input
            class="absolute -z-10 h-0 w-0 overflow-hidden opacity-0"
            type="text"
            name="more-details"
            placeholder="Tell us more about what you need:"
          />

          <div class="custom-textarea">
            <!-- this should be converted to a core-component -->
            <textarea
              id="colored-textarea-placeholder"
              class="text"
              name="comments"
              placeholder=" "
              bind:value={message}
            />
            <!-- here in the label you can use <span>(optional)</span> to paint the optional text if needed -->
            <label class="text" for="colored-textarea-placeholder"
              >{properties?.additionalDetails ||
                'Tell us more about what you need:'}
            </label>
            {#if messageError}
              <p class="text-md rounded-md bg-red-500 !p-4 text-white">
                Please provide your email or phone number
              </p>
            {:else if commentError}
              <p class="text-md rounded-md bg-red-500 !p-4 text-white">
                Please provide us some details so we can provide you with a
                quote
              </p>
            {/if}
          </div>

          <button
            type="submit"
            id="email_catcher_form_submit"
            class="primary-button"
          >
            {@html buttonText?.value}
          </button>
          <div class="bottom-boxes">
            <div class="small-text-box">
              {#each callouts.value as item}
                <div>
                  <BulletPoint bulletIcon={callouts?.icon} />
                  <p class="text-small">{@html item}</p>
                </div>
              {/each}
            </div>
          </div>
        </form>
      </div>
    {/if}
  </div>
</section>

<!-- {#if showSmsVerification && phone && leadId && formSubmitted}
  <SmsVerification {phone} {leadId} />
{/if} -->
